import { KalturaAssetType, KalturaAssetReferenceType } from '@kaltura-ott/ott-client-types/api/types';
import { ConnectivityType, getCurrentProgramByLinearList, getMediaAsset, KuxLinearType } from '@kaltura-ott/tvpil';
import { lastValueFrom } from 'rxjs';
import { container } from 'tsyringe';

import { DmsStore } from '../../dms';
import {
  addBookmarkToPlayable,
  addBookmarkToRecords,
  addCachedPurchaseToPlayable,
  addCurrentProgramToLinear,
  addPermanentProgramsToMosaicPrograms,
  addRentPossibilitiesToPlayable,
  addSubscriptionPossibilitiesToPlayable,
  creatorEntityStore,
} from '../../entities/entities.service';
import { Episode } from '../../entities/Episode';
import { Linear } from '../../entities/Linear';
import { Movie } from '../../entities/Movie';
import { PlayableAsset } from '../../entities/PlayableAsset';
import { Program } from '../../entities/Program';

export enum PlayoutStoreV2Type {
  LIVE = 'live',
  CATCHUP = 'catchup',
  START_OVER = 'startOver',
  VOD = 'vod',
  NPVR = 'npvr',
  WELCOME_POPUP = 'welcome_popup',
}

export const addBookmarkToPlayoutAsset = async (
  type: PlayoutStoreV2Type,
  asset: Program | Episode | Movie,
): Promise<Program | Episode | Movie> => {
  if (type === PlayoutStoreV2Type.CATCHUP || type === PlayoutStoreV2Type.START_OVER) {
    await addBookmarkToPlayable([asset]);
  }
  if (type === PlayoutStoreV2Type.NPVR && asset instanceof Program) {
    await addBookmarkToRecords([asset]);
  }
  if (type === PlayoutStoreV2Type.VOD && (asset instanceof Movie || asset instanceof Episode)) {
    await addBookmarkToPlayable([asset], KalturaAssetType.media);
  }

  return asset;
};

export const getFutureProgram = async (
  linearId: number,
  liveTimestamp?: number,
  loadMosaicPrograms?: boolean,
): Promise<Program | undefined> => {
  const dmsStore = container.resolve<DmsStore>('dmsStore');
  const programs = await lastValueFrom(
    getCurrentProgramByLinearList({
      linearIdList: [linearId],
      liveTimestamp,
      loadMosaicPrograms: dmsStore.dms.mosaicInfo.isMosaicSupported,
    }),
    {
      defaultValue: [],
    },
  );

  if (programs.length) {
    const dmsStore = container.resolve<DmsStore>('dmsStore');
    let currentProgram = programs[0];

    if (!loadMosaicPrograms && dmsStore.dms.mosaicInfo.isMosaicSupported && currentProgram.permanentProgram) {
      currentProgram = currentProgram.permanentProgram;
    }

    return creatorEntityStore(currentProgram);
  }
};

export const addDataToPlayoutLinear = async (
  linear: Linear,
  liveTimestamp?: number,
  loadMosaicPrograms?: boolean,
  skipRules = false,
): Promise<Linear> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const promises: Promise<any>[] = [addCurrentProgramToLinear([linear], liveTimestamp, true, loadMosaicPrograms)];

  if (!skipRules) {
    promises.push(linear.setupRules());
  }

  await Promise.all(promises);
  return linear;
};

export const addDataToPlayoutProgram = async (
  program: Program,
  type: PlayoutStoreV2Type,
  fromAutoplayMode = false,
): Promise<Program> => {
  const dataPromises = [
    program.setupRules(),
    program.linear?.setupRules(),
    addSubscriptionPossibilitiesToPlayable(program, program.price),
    addCachedPurchaseToPlayable(program),
    addPermanentProgramsToMosaicPrograms([program.data]),
  ];

  if (!fromAutoplayMode) {
    dataPromises.push(addBookmarkToPlayoutAsset(type, program));
  }

  await Promise.all(dataPromises);
  return program;
};

export const addDataToPlayoutVod = async (vod: Movie | Episode, type: PlayoutStoreV2Type): Promise<Movie | Episode> => {
  await Promise.all([
    addBookmarkToPlayoutAsset(type, vod),
    addSubscriptionPossibilitiesToPlayable(vod, vod.price),
    addRentPossibilitiesToPlayable([vod]),
    addCachedPurchaseToPlayable(vod),
    vod.setupRules(),
  ]);

  return vod;
};

export const getLinear = async (linearId: number): Promise<Linear | null> => {
  const kuxPlayableLinearAsset = await lastValueFrom(
    getMediaAsset<KuxLinearType>(linearId, KalturaAssetReferenceType.media),
  );

  if (kuxPlayableLinearAsset) {
    const nextAsset = creatorEntityStore(kuxPlayableLinearAsset) as PlayableAsset;
    return nextAsset as Linear;
  }

  return null;
};

const DEFAULT_NETWORK_KEY = 'networkMode';

export const getNetworkMode = async (endpoint?: string, key = DEFAULT_NETWORK_KEY): Promise<ConnectivityType> => {
  if (!endpoint) {
    throw new Error("Can't get network mode");
  }
  try {
    const res = await fetch(endpoint);
    const resJson = await res.json();
    return resJson[key] || ConnectivityType.ott;
  } catch (e) {
    return ConnectivityType.ott;
  }
};

let networkCache: { [cacheKey: string]: { value: ConnectivityType; ttl: number } } = {};
const NETWORK_CACHE_TTL = 3600000; // 60 min

export const clearNetworkCache = () => {
  networkCache = {};
};

export const getNetworkModeWithCache = async (
  endpoint?: string,
  key = DEFAULT_NETWORK_KEY,
): Promise<ConnectivityType> => {
  if (networkCache[`${endpoint}_${key}`] && networkCache[`${endpoint}_${key}`].ttl > Date.now()) {
    return networkCache[`${endpoint}_${key}`].value;
  }
  const newValue = await getNetworkMode(endpoint, key);
  networkCache[`${endpoint}_${key}`] = {
    value: newValue,
    ttl: Date.now() + NETWORK_CACHE_TTL,
  };
  return newValue;
};
